import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUrlParam, useUrlPanelSync } from '../hooks/useUrlParam';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import EmailBreakerToggle from '../components/email/EmailBreakerToggle';
import { Button, Badge, Dropdown, Icon, Table, SidePanel } from '../components/ui';
import type { TableColumn } from '../components/ui';
import { useUser } from '../context/UserContext';
import {
  listEmailItems,
  getPipelineRuns,
  isStale,
  agreeToEmailItem,
  disagreeWithEmailItem,
  requestSend,
  undoDecision,
  canUndo,
  subscribeEmailItems,
  gmailThreadUrl,
  categoryTone,
  actionTone,
  statusTone,
  age,
  excerpt,
  CATEGORY_LABELS,
  ACTION_LABELS,
  STATUS_LABELS,
} from '../lib/inbox';
import type {
  EmailItem,
  PipelineRun,
  EmailItemStatus,
  EmailCategory,
} from '../lib/inbox';

type StatusFilter = EmailItemStatus | 'all' | 'unread';
type CategoryFilter = EmailCategory | 'all';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  // Not a status — the is_unread flag, cutting across all of them.
  { value: 'unread', label: 'Unread in Gmail' },
  { value: 'agreed', label: 'Agreed' },
  { value: 'disagreed', label: 'Disagreed' },
  { value: 'executed', label: 'Executed' },
  { value: 'failed', label: 'Failed' },
  { value: 'all', label: 'All statuses' },
];

const CATEGORY_OPTIONS: Array<{ value: CategoryFilter; label: string }> = [
  { value: 'all', label: 'All categories' },
  ...(Object.keys(CATEGORY_LABELS) as EmailCategory[]).map(c => ({
    value: c as CategoryFilter,
    label: CATEGORY_LABELS[c],
  })),
];

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

// Escapes the Badge's uppercase so "3m" doesn't read as "3M" (three months)
const noTransform = { textTransform: 'none' } as const;

const panelStyle = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-lg)',
  padding: '48px 0',
  textAlign: 'center' as const,
  fontSize: 'var(--text-sm)',
};

export default function Inbox() {
  const { user, isUserLoaded } = useUser();
  const [items, setItems] = useState<EmailItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [runs, setRuns] = useState<{ sync: PipelineRun | null; analysis: PipelineRun | null }>({
    sync: null,
    analysis: null,
  });
  // Disagree opens a panel rather than acting immediately: the reason is the
  // whole point, so it cannot be an afterthought or a skippable prompt.
  const [disagreeItem, setDisagreeItem] = useState<EmailItem | null>(null);
  const [disagreeReason, setDisagreeReason] = useState('');
  const [disagreeError, setDisagreeError] = useState<string | null>(null);
  // The reply drawer. Sending is a second decision, taken here, never by Agree.
  const [replyItem, setReplyItem] = useState<EmailItem | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);

  const userRole = normalizeRole(user?.role);
  const hasAccess = userRole === 'admin' || userRole === 'developer' || userRole === 'agency_owner';

  const fetchItems = useCallback(async () => {
    try {
      // No account argument: RLS already limits this to the caller's own
      // mailbox, so asking for one here would be theatre.
      const rows = await listEmailItems({
        status: statusFilter,
        category: categoryFilter === 'all' ? null : categoryFilter,
      });
      setItems(rows);
      setLoadError(null);
    } catch (error) {
      console.error('Error fetching email items:', error);
      setLoadError('Could not load the email queue. Check the console for details.');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  const fetchRuns = useCallback(async () => {
    try {
      setRuns(await getPipelineRuns());
    } catch (error) {
      console.error('Error reading pipeline freshness:', error);
    }
  }, []);

  useEffect(() => {
    // Wait for isUserLoaded: it flips true only after the Supabase session is
    // restored, so fetching earlier silently returns zero rows under RLS.
    if (isUserLoaded && hasAccess) {
      fetchItems();
      fetchRuns();
    }
  }, [isUserLoaded, hasAccess, fetchItems, fetchRuns]);

  // Re-tick the freshness badges without refetching rows, so "4m ago" does not
  // sit frozen while the screen is open.
  useEffect(() => {
    const t = setInterval(fetchRuns, 60_000);
    return () => clearInterval(t);
  }, [fetchRuns]);

  // Live updates when a scan lands new marks or someone else agrees elsewhere
  useEffect(() => {
    if (!isUserLoaded || !hasAccess) return;
    return subscribeEmailItems(() => {
      fetchItems();
    });
  }, [isUserLoaded, hasAccess, fetchItems]);

  const openInGmail = (item: EmailItem) => {
    // A reply is the one row type where the useful thing is the draft, not the
    // original. Everything else still opens Gmail.
    if (item.proposed_action === 'reply' && item.recommended_reply) {
      setReplyParam(item.id);
      return;
    }
    window.open(gmailThreadUrl(item), '_blank', 'noopener,noreferrer');
  };

  const handleAgree = async (item: EmailItem) => {
    setBusyId(item.id);
    try {
      // Records approval; the local pipeline carries it out within ~5 minutes.
      // mark_read, trash and reply-drafting all run unattended (Kyle approved
      // that on 2026-09-05). SENDING never happens here or there — a reply
      // becomes a Gmail draft and waits for a person. escalate_comms writes a
      // Client Comms row and leaves the message itself untouched.
      await agreeToEmailItem(item.id, user?.id ?? null);
      await fetchItems();
    } catch (error) {
      console.error('Error agreeing to email item:', error);
      setLoadError('Could not record the agreement. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const openReply = (item: EmailItem) => {
    setReplyItem(item);
    setReplyText(item.recommended_reply ?? '');
    setReplyError(null);
  };

  // The reply under review is ?reply=<id>, so refresh and back keep the drawer.
  const [replyParam, setReplyParam] = useUrlParam('reply');
  useUrlPanelSync(
    replyParam,
    replyItem?.id ?? null,
    !isLoading,
    (id) => {
      const item = items.find((x) => x.id === id);
      if (item) openReply(item);
    },
    () => setReplyItem(null),
  );
  const closeReply = () => {
    setReplyItem(null);
    if (replyParam) setReplyParam(null);
  };

  const submitSend = async () => {
    if (!replyItem) return;
    if (!replyText.trim()) {
      setReplyError('Nothing to send.');
      return;
    }
    setBusyId(replyItem.id);
    try {
      await requestSend(replyItem.id, replyText);
      closeReply();
      await fetchItems();
    } catch (error) {
      console.error('Error requesting send:', error);
      setReplyError('Could not queue that. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleUndo = async (item: EmailItem) => {
    setBusyId(item.id);
    try {
      await undoDecision(item);
      await fetchItems();
    } catch (error) {
      console.error('Error undoing decision:', error);
      setLoadError('Could not undo that. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const openDisagree = (item: EmailItem) => {
    setDisagreeItem(item);
    setDisagreeReason('');
    setDisagreeError(null);
  };

  const submitDisagree = async () => {
    if (!disagreeItem) return;
    if (disagreeReason.trim().length < 3) {
      setDisagreeError('Tell me why, even briefly — the reason becomes a rule.');
      return;
    }
    setBusyId(disagreeItem.id);
    try {
      await disagreeWithEmailItem(
        disagreeItem.id,
        disagreeReason,
        disagreeItem.proposed_action,
        user?.id ?? null
      );
      setDisagreeItem(null);
      setDisagreeReason('');
      await fetchItems();
    } catch (error) {
      console.error('Error recording disagreement:', error);
      setDisagreeError('Could not save that. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      i =>
        i.from_email.toLowerCase().includes(query) ||
        i.from_name?.toLowerCase().includes(query) ||
        i.subject?.toLowerCase().includes(query) ||
        i.snippet?.toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  const columns: TableColumn<EmailItem>[] = [
    {
      key: 'from',
      label: 'From',
      width: '16%',
      render: (_v, row) => (
        <span style={{ display: 'block' }}>
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-sm)',
              fontWeight: row.is_unread ? 'var(--weight-medium)' : 'var(--weight-regular)',
              color: 'var(--text-body)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.from_name || row.from_email}
          </span>
          {row.from_name ? (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.from_email}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      width: '32%',
      wrap: true,
      render: (_v, row) => (
        <span style={{ display: 'block' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '6px',
              fontSize: 'var(--text-sm)',
              fontWeight: row.is_unread ? 'var(--weight-medium)' : 'var(--weight-regular)',
              color: 'var(--text-body)',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
          >
            <span title={row.subject ?? undefined}>{row.subject || '(no subject)'}</span>
            <a
              href={gmailThreadUrl(row)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="row-chevron"
              title="Open in Gmail"
            >
              <Icon name="external-link" size={12} />
            </a>
          </span>
          <span
            title={row.snippet ?? undefined}
            style={{
              display: 'block',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-faint)',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
          >
            {excerpt(row.snippet, 120)}
          </span>
        </span>
      ),
    },
    {
      key: 'received',
      label: 'Received',
      width: '8%',
      align: 'right',
      mono: true,
      render: (_v, row) => (
        <span title={new Date(row.received_at).toLocaleString()}>{age(row.received_at)}</span>
      ),
    },
    // No Account column: RLS means every row you can see is your own mailbox,
    // so the column would repeat a single value down the whole table.
    {
      key: 'category',
      label: 'Category',
      width: '11%',
      render: (_v, row) =>
        row.category ? (
          <Badge tone={categoryTone(row.category)}>{CATEGORY_LABELS[row.category]}</Badge>
        ) : (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Unmarked</span>
        ),
    },
    {
      key: 'proposed_action',
      label: 'Proposed action',
      width: '17%',
      wrap: true,
      // The AI's one-line reasoning rides under the badge — otherwise the
      // column asks the reviewer to trust a verdict with no stated why.
      render: (_v, row) => (
        <span style={{ display: 'block' }}>
          {row.proposed_action ? (
            <Badge tone={actionTone(row.proposed_action)}>{ACTION_LABELS[row.proposed_action]}</Badge>
          ) : (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>—</span>
          )}
          {row.reasoning ? (
            <span
              title={row.reasoning}
              style={{
                display: 'block',
                marginTop: '3px',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-faint)',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                lineHeight: 1.35,
              }}
            >
              {row.reasoning}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '16%',
      align: 'right',
      wrap: true,
      render: (_v, row) => renderActions(row),
    },
  ];

  function renderActions(row: EmailItem) {
    return (
        // stopPropagation on the wrapper: the row itself opens Gmail, and a
        // click on Agree/Disagree must not also punch a new tab open.
        <span
          onClick={e => e.stopPropagation()}
          style={{
            display: 'flex',
            gap: '6px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          {row.status === 'pending' ? (
            <>
              <Button
                size="sm"
                disabled={busyId === row.id}
                onClick={() => handleAgree(row)}
              >
                Agree
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busyId === row.id}
                onClick={() => openDisagree(row)}
              >
                Disagree
              </Button>
            </>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {/* A breaker decision must never read as one a person made. */}
              {row.auto_agreed ? (
                <span
                  title="Agreed automatically by the auto-agree breaker, not by a person"
                  style={{ display: 'inline-flex' }}
                >
                  <Badge tone="warn" style={noTransform}>
                    Auto
                  </Badge>
                </span>
              ) : null}
              <Badge tone={statusTone(row.status)} style={noTransform}>
                {STATUS_LABELS[row.status]}
              </Badge>
              {canUndo(row) ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === row.id}
                  onClick={() => handleUndo(row)}
                  title={
                    row.status === 'executed'
                      ? 'Reverse it in Gmail and put it back in Pending'
                      : 'Put it back in Pending'
                  }
                >
                  Undo
                </Button>
              ) : null}
            </span>
          )}
        </span>
    );
  }

  if (!isUserLoaded) return null;

  if (!hasAccess) {
    return <Navigate to="/clients" replace />;
  }

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Inbox"
          count={filteredItems.length}
          searchPlaceholder="Search sender, subject, snippet..."
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          actions={<EmailBreakerToggle />}
        />

        {/* Filters row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-5)',
          }}
        >
          <Badge
            tone={isStale(runs.sync) ? 'down' : 'up'}
            style={noTransform}
            title={runs.sync?.at ? new Date(runs.sync.at).toLocaleString() : 'never run'}
          >
            {runs.sync?.at
              ? `Synced ${age(runs.sync.at)} ago${runs.sync.rows != null ? ` · ${runs.sync.rows} pulled` : ''}`
              : 'Never synced'}
          </Badge>
          <Badge
            tone={isStale(runs.analysis) ? 'down' : 'up'}
            style={noTransform}
            title={runs.analysis?.at ? new Date(runs.analysis.at).toLocaleString() : 'never run'}
          >
            {runs.analysis?.at
              ? `Analyzed ${age(runs.analysis.at)} ago${
                  runs.analysis.unmarked ? ` · ${runs.analysis.unmarked} unmarked` : ''
                }`
              : 'Never analyzed'}
          </Badge>
          {/* Phones: the two filters split one row instead of stacking */}
          <div className="flex gap-2.5 w-full md:w-auto">
            <div className="flex-1 min-w-0 md:flex-none md:w-[170px]">
              <Dropdown
                size="sm"
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                style={{ width: '100%' }}
              />
            </div>
            {/* No account filter: RLS scopes every row to the signed-in user's
                own mailbox, so this control could only ever offer one value. */}
            <div className="flex-1 min-w-0 md:flex-none md:w-[190px]">
              <Dropdown
                size="sm"
                options={CATEGORY_OPTIONS}
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value as CategoryFilter)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-7 h-7 rounded-full animate-spin"
              style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : loadError ? (
          <div style={{ ...panelStyle, color: 'var(--status-down)' }}>{loadError}</div>
        ) : filteredItems.length === 0 ? (
          <div style={{ ...panelStyle, color: 'var(--text-faint)' }}>
            {searchQuery
              ? 'Nothing matches that search'
              : statusFilter === 'pending'
                ? 'Nothing to review — every marked email has been handled'
                : statusFilter === 'all'
                  ? 'No email has been scanned into the queue yet'
                  : statusFilter === 'unread'
                    ? 'Nothing unread — inbox zero, as of the last sync'
                    : `No ${STATUS_LABELS[statusFilter].toLowerCase()} messages`}
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <Table columns={columns} rows={filteredItems} onRowClick={openInGmail} />
            </div>
            {/* Phones: six fixed-width columns crushed every cell to a few
                characters, so each email stacks into one tappable card */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
              }}
            >
              {filteredItems.map((row, i) => (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  data-ck-inbox-row
                  className="hover-wash"
                  onClick={() => openInGmail(row)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') openInGmail(row);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    borderBottom:
                      i < filteredItems.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'var(--text-sm)',
                        fontWeight: row.is_unread ? 'var(--weight-medium)' : 'var(--weight-regular)',
                        color: 'var(--text-body)',
                      }}
                    >
                      {row.from_name || row.from_email}
                      {row.from_name ? (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                          {' '}· {row.from_email}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="t-num"
                      title={new Date(row.received_at).toLocaleString()}
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}
                    >
                      {age(row.received_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 'var(--text-sm)',
                        fontWeight: row.is_unread ? 'var(--weight-semibold)' : 'var(--weight-medium)',
                        color: 'var(--text-body)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.subject || '(no subject)'}
                    </span>
                    <a
                      href={gmailThreadUrl(row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      title="Open in Gmail"
                      aria-label="Open in Gmail"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        margin: '-9px -8px -9px 0',
                        flexShrink: 0,
                        color: 'var(--text-faint)',
                      }}
                    >
                      <Icon name="external-link" size={14} />
                    </a>
                  </div>
                  {row.snippet ? (
                    <span
                      className="line-clamp-2"
                      style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}
                    >
                      {row.snippet}
                    </span>
                  ) : null}
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                    {row.category ? (
                      <Badge tone={categoryTone(row.category)}>{CATEGORY_LABELS[row.category]}</Badge>
                    ) : (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Unmarked</span>
                    )}
                    {row.proposed_action ? (
                      <Badge tone={actionTone(row.proposed_action)}>{ACTION_LABELS[row.proposed_action]}</Badge>
                    ) : null}
                  </div>
                  {row.reasoning ? (
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        lineHeight: 1.4,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {row.reasoning}
                    </span>
                  ) : null}
                  {renderActions(row)}
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <SidePanel
        open={replyItem !== null}
        onClose={closeReply}
        eyebrow={replyItem ? `To ${replyItem.from_email}` : undefined}
        title="Review the reply"
        width={520}
        footer={
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Sending is this click. Nothing else sends.
            </span>
            <span style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <Button size="sm" variant="ghost" onClick={closeReply}>
                Cancel
              </Button>
              <Button size="sm" disabled={busyId === replyItem?.id} onClick={submitSend}>
                Send
              </Button>
            </span>
          </div>
        }
      >
        {replyItem ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                {/^re:/i.test(replyItem.subject || '')
                  ? replyItem.subject
                  : `Re: ${replyItem.subject || '(no subject)'}`}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {replyItem.from_name
                  ? `${replyItem.from_name} · ${replyItem.from_email}`
                  : replyItem.from_email}
              </div>
            </div>

            {replyItem.snippet ? (
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  borderLeft: '2px solid var(--border-hairline)',
                  paddingLeft: '10px',
                }}
              >
                {excerpt(replyItem.snippet, 220)}
              </div>
            ) : null}

            <textarea
              autoFocus
              value={replyText}
              onChange={e => {
                setReplyText(e.target.value);
                if (replyError) setReplyError(null);
              }}
              rows={10}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '10px 12px',
                fontFamily: 'inherit',
                fontSize: 'var(--text-sm)',
                lineHeight: 1.5,
                color: 'var(--text-body)',
                background: 'var(--surface-input, var(--surface-card))',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
              }}
            />

            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Your signature is appended when it goes out — don't type it here.
            </div>

            {replyError ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>
                {replyError}
              </div>
            ) : null}
          </div>
        ) : null}
      </SidePanel>

      <SidePanel
        open={disagreeItem !== null}
        onClose={() => setDisagreeItem(null)}
        eyebrow={disagreeItem ? `Proposed: ${ACTION_LABELS[disagreeItem.proposed_action ?? 'none']}` : undefined}
        title="Why is that wrong?"
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => setDisagreeItem(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busyId === disagreeItem?.id}
              onClick={submitDisagree}
            >
              Save reason
            </Button>
          </div>
        }
      >
        {disagreeItem ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                {disagreeItem.subject || '(no subject)'}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {disagreeItem.from_name
                  ? `${disagreeItem.from_name} · ${disagreeItem.from_email}`
                  : disagreeItem.from_email}
              </div>
            </div>

            {disagreeItem.reasoning ? (
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-muted)',
                  borderLeft: '2px solid var(--border-hairline)',
                  paddingLeft: '10px',
                }}
              >
                I said: {disagreeItem.reasoning}
              </div>
            ) : null}

            <textarea
              autoFocus
              value={disagreeReason}
              onChange={e => {
                setDisagreeReason(e.target.value);
                if (disagreeError) setDisagreeError(null);
              }}
              placeholder="e.g. Ahrefs digests matter to me, never mark them read"
              rows={5}
              style={{
                width: '100%',
                resize: 'vertical',
                padding: '10px 12px',
                fontFamily: 'inherit',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-body)',
                background: 'var(--surface-input, var(--surface-card))',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-md)',
                outline: 'none',
              }}
            />

            {disagreeError ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>
                {disagreeError}
              </div>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                Reasons are read back per mailbox and become classification rules, so
                the same mistake stops happening.
              </div>
            )}
          </div>
        ) : null}
      </SidePanel>
    </div>
  );
}
